import { useSettings } from "@/store/settings";
import { computed } from "vue";
import { APP_VARIANTS } from "@/variants";
import type { AppVariantKey, VariantConfig } from "@/variants";
import type { ComputedRef } from "vue";

/**
 * Composable per la variante applicativa corrente.
 *
 * `config` è la fonte di verità (variant registry): i componenti DEVONO
 * consumare flag semantici (`config.features.*`) e valori (`config.opportunities.*`).
 * `isGaia` / `isGaiaEni` / `filterByVariant` sono API legacy mantenute per
 * compatibilità: NON usarle in codice nuovo.
 */

type VariantType = AppVariantKey;

interface UseAppVariantReturn {
  config: ComputedRef<VariantConfig>;
  isGaiaEni: ComputedRef<boolean>;
  isGaia: ComputedRef<boolean>;
  appVariant: ComputedRef<VariantType>;
  platformHeader: ComputedRef<string>;
  currentApplicationId: ComputedRef<number>;
  filterByVariant: <T extends { visibleIn?: VariantType[]; [key: string]: any }>(items: T[]) => T[];
}

export const useAppVariant = (): UseAppVariantReturn => {
  const settingsStore = useSettings();

  const currentApplicationId = computed(() => settingsStore.effectiveApplicationId);

  const config = computed(() => settingsStore.effectiveVariantConfig);

  const appVariant = computed(() => config.value.key);

  const isGaiaEni = computed(() => appVariant.value === APP_VARIANTS.GaiaEni);
  const isGaia = computed(() => appVariant.value === APP_VARIANTS.Gaia);

  const platformHeader = computed(() => settingsStore.effectivePlatformIdHeader);

  /**
   * Filtra item (headers, colonne, campi...) in base alla variante corrente.
   * Item senza `visibleIn` sono visibili in tutte le varianti.
   *
   * @example
   * // Header visibile solo in GAIAENI
   * const header = { key: "commission", title: "Commission", visibleIn: ["gaiaeni"] }
   *
   * // Header visibile in entrambe (no visibleIn)
   * const header = { key: "name", title: "Name" }
   */
  const filterByVariant = <T extends { visibleIn?: VariantType[]; [key: string]: any }>(
    items: T[]
  ): T[] => {
    return items.filter((item) => {
      if (!item.visibleIn) {
        return true;
      }
      return item.visibleIn.includes(appVariant.value);
    });
  };

  return {
    config,
    isGaiaEni,
    isGaia,
    appVariant,
    platformHeader,
    currentApplicationId,
    filterByVariant,
  };
};
